import { useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import useCyclePurchaseEffectApi from "@apis/useCyclePurchaseEffectApi";
import useShowPDF from "@util/hook/useShowPDF";
import useValidationErrors from "@util/hook/useValidationErrors";
import { DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER, CLUB_MEMBERS_ROUTER } from "@icoach/router/routerPath";
import { MEMBERS_MEMBER_DETAIL_TABS } from "@icoach/constants/";
import { isEmpty } from "@util/utils";

/**
 * 循環訂單變更單提交 Hook
 * 負責處理變更單的新增/更新提交邏輯
 *
 * @param {Object} options - 設定選項
 * @param {number} [options.effectID=0] - 變更單ID（0 表示新增，>0 表示更新）
 * @param {number} [options.memberID=0] - 會員ID（用於成功後路由跳轉）
 * @param {Function} [options.onSuccess] - 成功後的回調（可選，會覆蓋預設行為）
 * @returns {Object} 提交函數和狀態
 */
const useCyclePurchaseEffectOrderSubmit = ({ effectID = 0, memberID = 0, onSuccess } = {}) => {
	const history = useHistory();
	const showPDF = useShowPDF();
	const { postCyclePurchaseEffectOrderApi, putCyclePurchaseEffectOrderApi } = useCyclePurchaseEffectApi();
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();
	const apiSendingRef = useRef(false);

	/**
	 * 提交變更單
	 * @param {Object} params - 提交參數
	 * @param {number} overrideEffectID - 覆蓋 effectID（可選）
	 * @returns {Promise<boolean>} 是否成功
	 */
	const submit = useCallback(
		async (params, overrideEffectID = null) => {
			if (apiSendingRef.current) return false;

			clearValidationErrors();
			apiSendingRef.current = true;

			try {
				const currentEffectID = overrideEffectID !== null ? overrideEffectID : effectID;
				let resp;

				// 判斷 effectID 是否 > 0，來決定是「更新」還是「新增」
				if (Number(currentEffectID) && currentEffectID > 0) {
					// 更新現有變更單
					resp = await putCyclePurchaseEffectOrderApi(params, currentEffectID);
				} else {
					// 新增變更單
					resp = await postCyclePurchaseEffectOrderApi(params);
				}

				const { isError = false, result, completedPath } = resp || {};

				if (isError) {
					// 0002 錯誤
					updateValidationErrors(result);
					apiSendingRef.current = false;
					return false;
				} else if (isEmpty(resp)) {
					// 失敗
					apiSendingRef.current = false;
					return false;
				} else {
					// 成功
					if (onSuccess && typeof onSuccess === "function") {
						// 自訂成功處理
						onSuccess(resp, params);
					} else {
						// 預設成功處理
						const finalMemberID = memberID || params.memberID || 0;
						let routerPath = DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.BASE;
						if (currentEffectID) {
							routerPath = CLUB_MEMBERS_ROUTER.MEMBER_DETAIL.GET_TABS_PATH(finalMemberID, MEMBERS_MEMBER_DETAIL_TABS.HC);
						}

						// 開啟PDF
						if (completedPath) {
							showPDF({
								open: true,
								pdfUrl: completedPath,
							});
						}

						// 轉跳
						window.setTimeout(() => {
							history.replace(routerPath);
						}, 5);
					}

					apiSendingRef.current = false;
					return true;
				}
			} catch (error) {
				console.error("提交變更單失敗:", error);
				apiSendingRef.current = false;
				return false;
			}
		},
		// eslint-disable-next-line
		[effectID, memberID, postCyclePurchaseEffectOrderApi, putCyclePurchaseEffectOrderApi, updateValidationErrors, clearValidationErrors, onSuccess, showPDF, history],
	);

	/**
	 * 檢查是否正在提交中
	 */
	const isSubmitting = useCallback(
		() => {
			return apiSendingRef.current;
		},
		// eslint-disable-next-line
		[],
	);

	return {
		submit,
		isSubmitting,
		validationErrors,
		clearValidationErrors,
	};
};

export default useCyclePurchaseEffectOrderSubmit;
